/**
 * Sourcing Call detail — open thread. The buyer/admin can pick a winning
 * reply; suppliers and other signed-in users can read every reply.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, MessageSquare, MapPin, Clock, ArrowLeft, Trophy, Check } from 'lucide-react';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { devError } from '../utils/logger';
import { toast } from 'sonner';

export default function SourcingCallDetailPage() {
  const { call_id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sourcing-calls/${call_id}`);
      setData(r.data);
    } catch (e) {
      devError('call detail', e);
      if (e.response?.status === 404) setData({ notFound: true });
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user) refresh(); }, [user, call_id]);

  if (authLoading || loading) {
    return <div className="min-h-screen pt-24 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>;
  }
  if (!user) return <Navigate to={`/login?next=/sourcing/${call_id}`} replace />;
  if (data?.notFound) return <NotFound />;
  if (!data) return null;

  const { call, replies, is_owner } = data;
  const closeWithWinner = async (reply_id) => {
    setPicking(reply_id);
    try {
      await axios.post(`${API}/sourcing-calls/${call_id}/close`, { winning_reply_id: reply_id });
      toast.success('Winner picked — call marked fulfilled');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setPicking(null); }
  };

  const remaining = formatRemaining(call.deadline_at);

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="call-detail-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Link to="/sourcing" className="text-xs text-muted-foreground hover:text-accent inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-3 h-3" /> Back to Sourcing Calls
        </Link>

        <div className="rounded-xl border border-border/30 bg-card/30 p-6 mb-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <h1 className="font-playfair text-3xl font-bold">{call.title}</h1>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
              call.status === 'open' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' :
              call.status === 'fulfilled' ? 'bg-accent/15 text-accent border border-accent/30' :
              'bg-muted text-muted-foreground'
            }`}>{call.status}</span>
          </div>
          <p className="text-foreground/80 whitespace-pre-wrap mb-4">{call.description}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {call.category && <span>📦 {call.category}</span>}
            {call.quantity && <span>🔢 {call.quantity}</span>}
            {call.location && <span><MapPin className="w-3 h-3 inline mr-0.5" />{call.location}</span>}
            {call.needed_by && <span>⏰ Needed: {call.needed_by}</span>}
            <span><Clock className="w-3 h-3 inline mr-0.5" />{remaining}</span>
          </div>
        </div>

        {is_owner ? (
          <div>
            <h2 className="font-playfair text-xl mb-4 inline-flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-accent" />
              Replies — pick a winner when you're ready
            </h2>
            {replies.length === 0 ? (
              <p className="text-muted-foreground py-12 text-center">No replies yet — suppliers have until <strong className="text-foreground/80">{remaining}</strong> to respond.</p>
            ) : (
              <div className="space-y-4" data-testid="replies-list">
                {replies.map((r) => (
                  <article key={r.reply_id} className={`rounded-xl border p-5 ${
                    r.reply_id === call.winning_reply_id
                      ? 'border-accent/50 bg-accent/5 ring-2 ring-accent/30'
                      : 'border-border/30 bg-card/30'
                  }`} data-testid={`reply-${r.reply_id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="font-playfair text-lg">{r.supplier_name}</p>
                        <p className="text-[11px] text-muted-foreground">{r.supplier_email} · {new Date(r.submitted_at).toLocaleString()}</p>
                      </div>
                      {r.price_aud != null && (
                        <p className="text-2xl font-bold text-accent">${r.price_aud.toLocaleString()}</p>
                      )}
                    </div>
                    <p className="text-sm text-foreground/85 whitespace-pre-wrap mb-4">{r.message}</p>
                    {call.status === 'open' && (
                      <Button
                        size="sm"
                        onClick={() => closeWithWinner(r.reply_id)}
                        disabled={picking === r.reply_id}
                        className="bg-accent hover:bg-accent/90"
                        data-testid={`pick-winner-${r.reply_id}`}
                      >
                        {picking === r.reply_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}
                        Pick this reply as the winner
                      </Button>
                    )}
                    {r.reply_id === call.winning_reply_id && (
                      <p className="text-xs text-accent inline-flex items-center gap-1"><Check className="w-3 h-3" />Picked as winner</p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <h2 className="font-playfair text-xl mb-4 inline-flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-accent" />
              Open thread — {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
            </h2>
            {replies.length === 0 ? (
              <p className="text-muted-foreground py-12 text-center">
                No replies yet. {remaining && <>Open for {remaining}.</>}
              </p>
            ) : (
              <div className="space-y-4" data-testid="replies-list">
                {replies.map((r) => (
                  <article key={r.reply_id} className="rounded-xl border border-border/30 bg-card/30 p-5" data-testid={`reply-${r.reply_id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="font-playfair text-lg">{r.supplier_name}</p>
                        <p className="text-[11px] text-muted-foreground">{new Date(r.submitted_at).toLocaleString()}</p>
                      </div>
                      {r.price_aud != null && (
                        <p className="text-2xl font-bold text-accent">${r.price_aud.toLocaleString()}</p>
                      )}
                    </div>
                    <p className="text-sm text-foreground/85 whitespace-pre-wrap">{r.message}</p>
                    {r.reply_id === call.winning_reply_id && (
                      <p className="mt-3 text-xs text-accent inline-flex items-center gap-1"><Check className="w-3 h-3" />Picked as winner</p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen pt-24 px-6 text-center">
      <h1 className="font-playfair text-3xl mb-2">Sourcing call not found</h1>
      <p className="text-muted-foreground mb-6">It may have been closed or the link is invalid.</p>
      <Link to="/sourcing" className="text-accent hover:underline">Back to Sourcing Calls</Link>
    </div>
  );
}

function formatRemaining(deadlineISO) {
  if (!deadlineISO) return '';
  const ms = new Date(deadlineISO).getTime() - Date.now();
  if (ms <= 0) return 'closed';
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${minutes}m left`;
}
